import { AccordionProps, Button } from '@toggle/design-system';
import { AssetClassCode, periods } from '@toggle/toggle';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  filterPredictions,
  getInsightCondition,
  getInsightTotalTradingDays,
} from '~/api/insights/utils/insight-utils';
import { ConditionSection } from '~/components/condition-section/ConditionSection';
import { PriceMovementChart } from '~/components/price-movement/components/price-movement-chart/PriceMovementChart';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useTerminalNavigate } from '~/hooks/use-terminal-navigate/useTerminalNavigate';
import { EntityHeader } from '~/modules/entity-header/EntityHeader';
import { InsightWidgetData } from '~/stores/use-chat/use-chat.types';
import { getPredictionsTableData } from '~/views/turbo-chart/components/insights/utils/inisights-utils/insights-utils';
import { AssessmentWidget } from '~/widgets/assessment-widget/AssessmentWidget';
import { InsightPastEpisodes } from '~/widgets/past-episodes-widget/components/insight-past-episodes/InsightPastEpisodes';
import { PredictionSummaryTable } from '~/widgets/scenario/components/prediction-summary-table/PredictionSummaryTable';

import { InsightSummary } from '../../../insight-summary/InsightSummary';
import { InsightsReaction } from '../insights-reaction/InisghtsReaction';
import * as S from './InsightsDrawerContent.styles';

const IDX_MAX = 126;
const ACCORDION_ANIM_DURATION = 200;
const MIN_SPACE_UNDER_ACCORDION = 100;

export type InsightsDrawerContentProps = {
  insightsData: InsightWidgetData;
};
export const InsightsDrawerContent = ({
  insightsData,
}: InsightsDrawerContentProps) => {
  const { t } = useTranslation(['scenarioCommander', 'widget']);
  const { insight, predictions, entity } = insightsData;
  const ref = useRef<HTMLDivElement>(null);

  // Temp: update to navigate from watchlist when available
  const navigateTerminal = useTerminalNavigate();
  const { navigateToPage } = useNavigateToAssetOverview();

  const navigateToAssetOverview = () => {
    navigateTerminal({
      navigateFn: () => navigateToPage({ entityTag: entity.tag }),
      crumb: { label: '', assetTag: entity.tag },
    });
  };

  const entityName = entity.name;
  const isFixedIncome =
    entity.asset_class === AssetClassCode.Fi && entity.class === 6;

  const episodeCount = insight.episodes.length;
  const totalTradingDays = getInsightTotalTradingDays(insight);
  const condition = getInsightCondition(insight, t);

  const horizons = periods;
  const predictionByHorizon = useMemo(() => {
    return getPredictionsTableData(horizons, predictions);
  }, [predictions, horizons]);

  const onAccordionItemClick: AccordionProps['onItemClick'] = ({
    event,
    isExpanded,
  }) => {
    const target = event.currentTarget as HTMLDivElement;
    const container = ref.current;

    if (isExpanded && container) {
      setTimeout(() => {
        // this check is required as browser saves scroll position
        // so there can be a case when we don't need to scroll
        const spaceUnder =
          container.offsetHeight - (target.offsetTop - container.scrollTop);
        const shouldScroll = spaceUnder < MIN_SPACE_UNDER_ACCORDION;
        if (shouldScroll) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      }, ACCORDION_ANIM_DURATION);
    }
  };

  return (
    <S.Container ref={ref}>
      <EntityHeader
        entity={entity}
        truncateLongTitle
        headerButton={
          <Button
            iconName="Expand"
            label={t('widget:assetOverview.assetOverview')}
            onClick={navigateToAssetOverview}
            variant="tertiary"
            size="small"
          />
        }
      />
      <InsightSummary insight={insight} isFixedIncome={isFixedIncome} />
      <ConditionSection
        condition={condition}
        episodesCount={episodeCount}
        totalTradingDays={totalTradingDays}
      />
      <InsightsReaction
        entityName={entityName}
        episodeCount={episodeCount}
        isFixedIncome={isFixedIncome}
      />
      <PriceMovementChart
        prediction={filterPredictions(insightsData, IDX_MAX).predictions}
        horizonToIndexOffset={1}
        bestHorizon={insight.metadata.horizon}
        isFixedIncome={isFixedIncome}
        useDynamicDaysLabel
      />
      <PredictionSummaryTable
        data={predictionByHorizon}
        isMedian
        horizons={horizons}
        isFixedIncome={isFixedIncome}
      />
      <S.StyledAccordion
        withBackground={false}
        itemClassName="insights-accordion"
        items={[
          {
            title: t('widget:pastEpisode.pastEpisode'),
            content: (
              <InsightPastEpisodes
                insight={insight}
                entity={entity}
                variant={'insight-past-episodes'}
              />
            ),
          },
        ]}
        duration={ACCORDION_ANIM_DURATION}
        onItemClick={onAccordionItemClick}
      />
      <S.StyledAccordion
        withBackground={false}
        itemClassName="insights-accordion"
        items={[
          {
            title: t('widget:assessment.title'),
            content: (
              <>
                <AssessmentWidget
                  isFixedIncome={isFixedIncome}
                  gaugeValue={insight.metadata.stars}
                  horizon={insight.metadata.horizon}
                  median={insight.metadata.median_return}
                  checklist={insight.metadata.high_conviction_report}
                  hasHeader={false}
                />
                <S.AssessmentPadding />
              </>
            ),
          },
        ]}
        duration={ACCORDION_ANIM_DURATION}
        onItemClick={onAccordionItemClick}
      />
    </S.Container>
  );
};
